import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaUser, FaUsers } from "react-icons/fa";
import { Globe, Home, Plus, Search, Share2 } from "lucide-react";
import { RootState } from "../store";
import { useSelector } from "react-redux";
import { getRequest } from "../helpers/api.helper";
import { useLanguage } from "../context/LanguageContext";
import BiodataUploadModal from "../pages/UserCreation/BiodataUploadModal";

const NAV_H = 82;

const BottomNavbar = () => {
  const bureauInfo = useSelector((state: RootState) => state.bureau.data);
  const { t } = useLanguage();
  const [isAddProfileModalOpen, setIsAddProfileModalOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const [expandedOption, setExpandedOption] = useState<string | null>(null);
  const [isMediatorProfile, setIsMediatorProfile] = useState(false);
  const [activeProfileType, setActiveProfileType] = useState<string | null>(
    null,
  );
  const [searchMobile, setSearchMobile] = useState("");

  // ✅ NEW: AI / Manual selection state (same as Gnavbar)
  const [addMode, setAddMode] = useState<"ai" | null>(null);
  const [searchMobileError, setSearchMobileError] = useState("");
  const [searchMobileLoading, setSearchMobileLoading] = useState(false);

  const openAddProfileModal = () => {
    setExpandedOption(null);
    setAddMode(null);
    setIsMediatorProfile(false);
    setIsAddProfileModalOpen(true);
  };

  const closeAddProfileModal = () => {
    setIsAddProfileModalOpen(false);
    setExpandedOption(null);
    setAddMode(null);
    setIsMediatorProfile(false);
    setActiveProfileType(null);
    setSearchMobileError("");
  };

  const handleSearchMobile = () => {
    const cleaned = searchMobile.replace(/\D/g, "").slice(0, 10);
    if (!cleaned) {
      setSearchMobileError("Please enter mobile number.");
      return;
    }
    setSearchMobileError("");
    setSearchMobileLoading(true);
    getRequest(`/user/profile/by-mobile/${cleaned}`)
      .then((response) => {
        const list = Array.isArray(response?.data) ? response.data : [];
        if (response?.success && list.length > 0) {
          closeAddProfileModal();
          navigate("/admin/search/mobile", { state: { mobile: cleaned } });
          return;
        }
        setSearchMobileError(
          response?.isDeleted
            ? "This profile is deleted"
            : "No profile found with that mobile number.",
        );
      })
      .catch(() => {
        setSearchMobileError("Something went wrong. Please try again.");
      })
      .finally(() => {
        setSearchMobileLoading(false);
      });
  };

  const currentPath = location.pathname;
  const mobileBottomNavVisibleRoutes = new Set([
    "/admin/dashboard",
    "/admin/profiles/callback",
  ]);
  const shouldHideBottomNav = !mobileBottomNavVisibleRoutes.has(currentPath);
  const bureauPublicPath =
    bureauInfo?.bureauId && bureauInfo?.bureauName
      ? `/${bureauInfo.bureauId}/${bureauInfo.bureauName.replace(/\s+/g, "")}`
      : null;

  const isHomeActive = currentPath === "/admin/dashboard";
  const isSearchActive =
    currentPath === "/admin/search" || currentPath.startsWith("/admin/search/");
  const isAddProfileActive = currentPath.startsWith("/admin/user/profile/");
  const isShareActive = currentPath === "/admin/share";
  const isMyWebActive = bureauPublicPath
    ? currentPath === bureauPublicPath
    : false;

  const navItemClass = (isActive: boolean) =>
    `flex min-w-0 flex-col items-center justify-end gap-1 pb-[2px] transition-all duration-300 ${
      isActive ? "text-white" : "text-white/90"
    }`;

  const navLabelClass = (isActive: boolean) =>
    `whitespace-nowrap text-[11px] leading-none ${
      isActive ? "font-semibold" : "font-medium"
    }`;

  useEffect(() => {
    if (shouldHideBottomNav) {
      document.body.classList.remove("has-bottom-nav");

      return;
    }

    document.body.classList.add("has-bottom-nav");

    return () => {
      document.body.classList.remove("has-bottom-nav");
    };
  }, [shouldHideBottomNav]);

  if (shouldHideBottomNav) {
    return null;
  }

  return (
    <nav className="bottom-nav fixed inset-x-0 bottom-0 z-[9999] md:hidden">
      <div
        className="relative w-full overflow-visible"
        style={{
          height: "calc(94px + var(--bottom-safe-area))",

          paddingBottom: "var(--bottom-safe-area)",
        }}
      >
        {/* solid background behind Android safe-area */}

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0"
          style={{
            height: "calc(70px + var(--bottom-safe-area))",

            background:
              "linear-gradient(90deg,#9900E7 0%,#C100F2 52%,#9800D8 100%)",
          }}
        />

        <svg
          className="pointer-events-none absolute inset-x-0 top-0 h-[94px] w-full"
          viewBox="0 0 500 94"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="bottomNavGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#9900E7" />

              <stop offset="52%" stopColor="#C100F2" />

              <stop offset="100%" stopColor="#9800D8" />
            </linearGradient>
          </defs>

          <path
            d="

          M0 24

          H190

          C215 24 225 64 250 64

          C275 64 285 24 310 24

          H500

          V94

          H0

          Z

        "
            fill="url(#bottomNavGradient)"
          />
        </svg>

        {/* Floating Add Profile circle */}
        <button
          type="button"
          onClick={openAddProfileModal}
          className="absolute left-1/2 top-0 z-30 -translate-x-1/2 -translate-y-[4px]"
        >
          <span
            className={`inline-flex h-[68px] w-[68px] items-center justify-center rounded-full
            border-[8px] border-[#F8FAFC] text-white
            shadow-[0_8px_20px_rgba(0,0,0,0.22)]
            ${
              isAddProfileActive
                ? "bg-[linear-gradient(135deg,#F26A3D_0%,#DB4A2E_100%)]"
                : "bg-[linear-gradient(135deg,#F27449_0%,#DB4A2E_100%)]"
            }`}
          >
            <Plus size={32} strokeWidth={2.8} />
          </span>
        </button>

        {/* Existing text and actions unchanged */}
        <div className="relative z-20 grid h-[94px] grid-cols-5 items-end px-2 pb-3 pt-6">
          <button
            className={navItemClass(isHomeActive)}
            onClick={() => navigate("/admin/dashboard")}
          >
            <Home size={23} strokeWidth={2.6} />
            <span className={navLabelClass(isHomeActive)}>
              {t("bottomNav.home")}
            </span>
          </button>

          <button
            className={navItemClass(isSearchActive)}
            onClick={() => navigate("/admin/search")}
          >
            <Search size={23} strokeWidth={2.5} />
            <span className={navLabelClass(isSearchActive)}>
              {t("bottomNav.search")}
            </span>
          </button>

          <button
            type="button"
            onClick={openAddProfileModal}
            className="relative flex min-w-0 items-end justify-center pb-[2px] text-white"
          >
            <span
              className={`absolute bottom-[1px] whitespace-nowrap text-[11px] leading-none ${
                isAddProfileActive ? "font-semibold" : "font-medium"
              }`}
            >
              {t("bottomNav.addProfile")}
            </span>
          </button>

          <button
            className={navItemClass(isShareActive)}
            onClick={() => navigate("/admin/share")}
          >
            <Share2 size={23} strokeWidth={2.5} />
            <span className={navLabelClass(isShareActive)}>
              {t("bottomNav.shareUrl")}
            </span>
          </button>

          <button
            className={navItemClass(isMyWebActive)}
            onClick={() =>
              navigate(
                `/${bureauInfo?.bureauId}/${bureauInfo?.bureauName?.replace(
                  /\s+/g,
                  "",
                )}`,
              )
            }
          >
            <Globe size={23} strokeWidth={2.5} />
            <span className={navLabelClass(isMyWebActive)}>
              {t("bottomNav.myWeb")}
            </span>
          </button>
        </div>
      </div>

      {isAddProfileModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-50">
          <div className="bg-white p-8 rounded-lg w-96 max-w-md">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <FaUser className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("bottomNav.modal.title")}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {t("bottomNav.modal.description")}
              </p>

              {expandedOption === null && (
                <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="mb-4">
                    <h3 className="text-base font-semibold text-gray-800">
                      Search by Mobile Number
                    </h3>
                    {/* <p className="mt-1 text-sm text-gray-500">
                      Enter a registered mobile number to find profiles.
                    </p> */}
                  </div>

                  <div className="space-y-3">
                    <input
                      type="tel"
                      value={searchMobile}
                      onChange={(e) =>
                        setSearchMobile(
                          e.target.value.replace(/\D/g, "").slice(0, 10),
                        )
                      }
                      placeholder="Enter 10-digit mobile number"
                      className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-purple-500 focus:ring-4 focus:ring-purple-100"
                      maxLength={10}
                    />

                    {searchMobileError && (
                      <p className="text-xs font-medium text-red-600">
                        {searchMobileError}
                      </p>
                    )}

                    <button
                      onClick={handleSearchMobile}
                      disabled={searchMobileLoading}
                      className={`w-full rounded-xl py-3 text-sm font-semibold transition ${
                        searchMobileLoading
                          ? "cursor-not-allowed bg-gray-200 text-gray-500"
                          : "bg-purple-600 text-white hover:bg-purple-700 active:scale-[0.99]"
                      }`}
                    >
                      {searchMobileLoading ? "Searching..." : "Search"}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {expandedOption === null && (
                  <>
                    {/* --> OWN PROFILE BUTTON */}
                    <button
                      onClick={() => {
                        // setExpandedOption("own");
                        setAddMode(null);
                        navigate("/admin/user/profile/basic/own");
                      }}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-md flex items-center justify-center mb-8"
                    >
                      <FaUser className="mr-2" /> {t("bottomNav.modal.own")}
                    </button>

                    {/* --> MEDIATOR PROFILE BUTTON */}
                    <button
                      onClick={() => {
                        // setExpandedOption("mediator");
                        setAddMode(null);
                        navigate("/admin/user/profile/basic/mediator");
                      }}
                      className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 px-4 rounded-md flex items-center justify-center"
                    >
                      <FaUsers className="mr-2" />{" "}
                      {t("bottomNav.modal.mediator")}
                    </button>
                  </>
                )}

                {/* Own Profile Submenu */}
                {expandedOption === "own" && (
                  <div className="space-y-2 mt-2">
                    {/* Step 1: Choose AI / Manual */}
                    {addMode === null && (
                      <>
                        <button
                          onClick={() => {
                            setExpandedOption(null);
                            setAddMode(null);
                          }}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          onClick={() => {
                            closeAddProfileModal();
                            navigate("/admin/user/profile/basic/own");
                          }}
                          className="w-full bg-purple-600 text-white py-3 rounded-md font-medium hover:bg-purple-700 transition"
                        >
                          {t("bottomNav.modal.addBasic")}
                        </button>

                        {/* <button
                          onClick={() => {
                            setIsAddProfileModalOpen(false);
                            navigate("/admin/user/profile/own");
                          }}
                          className="w-full bg-red-200 text-gray-800 py-3 rounded-md font-medium hover:bg-red-300 transition"
                        >
                          {t("bottomNav.modal.addFullRecommended")}
                        </button> */}
                      </>
                    )}

                    {/* Step 2: AI => Text / Image */}
                    {addMode === "ai" && (
                      <>
                        <button
                          onClick={() => setAddMode(null)}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          onClick={() => {
                            setIsAddProfileModalOpen(false);
                            navigate("/admin/user/profile/text", {
                              state: { isMediatorProfile: false },
                            });
                          }}
                          className="w-full bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 transition"
                        >
                          Paste Biodata Text
                        </button>

                        <button
                          onClick={() => {
                            setIsAddProfileModalOpen(false);
                            setIsMediatorProfile(false);
                            setActiveProfileType("biodata-upload");
                          }}
                          className="w-full bg-green-600 text-white py-3 rounded-md font-medium hover:bg-green-700 transition"
                        >
                          Upload Biodata Image
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Mediator Profile Submenu */}
                {expandedOption === "mediator" && (
                  <div className="space-y-2 mt-2">
                    {/* Step 1: Choose AI / Manual */}
                    {addMode === null && (
                      <>
                        <button
                          onClick={() => {
                            setExpandedOption(null);
                            setAddMode(null);
                          }}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          onClick={() => {
                            closeAddProfileModal();
                            navigate("/admin/user/profile/basic/mediator");
                          }}
                          className="w-full bg-purple-600 text-white py-3 rounded-md font-medium hover:bg-purple-700 transition"
                        >
                          {t("bottomNav.modal.addBasic")}
                        </button>

                        {/* <button
                          onClick={() => {
                            setIsAddProfileModalOpen(false);
                            navigate("/admin/user/profile/mediator");
                          }}
                          className="w-full bg-red-200 text-gray-800 py-3 rounded-md font-medium hover:bg-red-300 transition"
                        >
                          {t("bottomNav.modal.addFullRecommended")}
                        </button> */}
                      </>
                    )}

                    {/* Step 2: AI => Text / Image */}
                    {addMode === "ai" && (
                      <>
                        <button
                          onClick={() => setAddMode(null)}
                          className="w-full text-sm text-purple-600 underline"
                        >
                          ← {t("Back")}
                        </button>

                        <button
                          onClick={() => {
                            setIsAddProfileModalOpen(false);
                            navigate("/admin/user/profile/text", {
                              state: { isMediatorProfile: true },
                            });
                          }}
                          className="w-full bg-blue-600 text-white py-3 rounded-md font-medium hover:bg-blue-700 transition"
                        >
                          Paste Biodata Text
                        </button>

                        <button
                          onClick={() => {
                            setIsAddProfileModalOpen(false);
                            setIsMediatorProfile(true);
                            setActiveProfileType("biodata-upload");
                          }}
                          className="w-full bg-green-600 text-white py-3 rounded-md font-medium hover:bg-green-700 transition"
                        >
                          Upload Biodata Image
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={closeAddProfileModal}
                className="mt-4 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-300"
              >
                {t("bottomNav.modal.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeProfileType === "biodata-upload" && (
        <BiodataUploadModal
          onClose={() => {
            setActiveProfileType(null);
            setIsMediatorProfile(false); // Reset after closing
          }}
          bureauId={bureauInfo?.bureauId || ""}
          isMediatorProfile={isMediatorProfile}
        />
      )}
    </nav>
  );
};

export default BottomNavbar;
